import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./i18n/i18n";
import "./hotjar/hotjar";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { CssBaseline } from "@mui/material";
import { Provider } from "react-redux";
import store, { persistor } from "./store/store";
import { PersistGate } from "redux-persist/integration/react";
import SnackbarProvider from "./providers/SnackbarProvider";
import ErrorFallback from "./components/error/ErrorFallback";
import { ErrorBoundary } from "react-error-boundary";
import { ThemeModeProvider } from "./theme/ThemeModeProvider";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es"; // Spanish locale
import "dayjs/locale/en"; // English locale
import Clarity from "@microsoft/clarity";
import numeral from "numeral";
import {
  applyBrandingToDocument,
  useTenantBranding,
} from "./utils/tenantBranding";
numeral.register("locale", "es", {
  delimiters: { thousands: ".", decimal: "," },
  abbreviations: { thousand: "k", million: "m", billion: "b", trillion: "t" },
  ordinal: () => "º",
  currency: { symbol: "€" },
});
numeral.locale("es");

const env = process.env.REACT_APP_ENV;

if (process.env.REACT_APP_MICROSOFT_CLARITY_ID) {
  Clarity.init(process.env.REACT_APP_MICROSOFT_CLARITY_ID);
}

if (env === "production") {
  Sentry.init({
    dsn: "https://306f6293c72319603f2ba2f0bfd11468@o4509491213303808.ingest.de.sentry.io/4509491214352464",
    sendDefaultPii: true,
    ignoreErrors: [
      "Failed to fetch",
      "No user is logged in",
      "auth/invalid-credential",
      "auth/network-request-failed",
      "Network Error",
      "Network request failed",
    ],
    beforeSend(event, hint) {
      const error = hint.originalException;

      // If someone threw an object with status/data instead of an Error
      if (
        error &&
        typeof error === "object" &&
        !("name" in error) &&
        ("status" in error || "data" in error)
      ) {
        const anyErr = error as Record<string, unknown>;
        event.message = `API Error: ${anyErr.status || "Unknown status"} - ${
          typeof anyErr.data === "string"
            ? anyErr.data
            : JSON.stringify(anyErr.data)
        }`;

        if (event.exception?.values?.[0]) {
          event.exception.values[0].type = "APIResponseError";
          event.exception.values[0].value = event.message;
        }
      }

      // Cleanup generic React unhandled errors or improve titles
      if (event.exception?.values?.[0]) {
        const exception = event.exception.values[0];

        // Make "Cannot read properties of undefined" more readable
        if (exception.value?.includes("Cannot read properties of undefined")) {
          const match = exception.value.match(/reading '([^']+)'/);
          if (match && match[1]) {
            exception.type = "UndefinedPropertyError";
            exception.value = `Attempted to read undefined property '${match[1]}'`;
          }
        }
      }

      return event;
    },
  });
}

dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const BrandingAwareApp: React.FC = () => {
  const branding = useTenantBranding();

  useEffect(() => {
    applyBrandingToDocument(branding);
  }, [branding]);

  return (
    <ThemeModeProvider>
      <Provider store={store}>
        <PersistGate loading={<div>Loading...</div>} persistor={persistor}>
          <SnackbarProvider>
            <CssBaseline />
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <App />
            </ErrorBoundary>
          </SnackbarProvider>
        </PersistGate>
      </Provider>
    </ThemeModeProvider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <React.StrictMode>
    <BrandingAwareApp />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
